/**
 * Date Formatting Utilities
 *
 * Utility functions for formatting dates in various formats
 * for the Personal Trainer App.
 *
 * This module is the only way into dates: every package imports `dayjs` from
 * here (never from 'dayjs', which would skip the plugins and the Monday week
 * start below), and the native `Date` is banned by the `do-dates/no-native-date`
 * lint rule. The conventions:
 * - Date parameters take `DateInput`; held and returned values are `dayjs.Dayjs`;
 *   stores and anything persisted keep ISO strings or epoch milliseconds.
 * - `dayjs().valueOf()` for app timestamps. `performance.now()` only for
 *   durations that live in memory (gesture velocity, debounce).
 * - A library that needs a real `Date` gets `dayjs(...).toDate()` at the call.
 * - Read "now" where it is used, never while a module loads (the
 *   `do-dates/no-load-time-now` rule): schema bounds take `() => dayjs()`, and a
 *   rendered "today" in web comes from `useToday`.
 * - Sending dates to the API: a moment goes as `toISOString()` (UTC, `Z`) or as
 *   epoch milliseconds, never as `dayjs().format()` or a naive local string. A
 *   calendar date goes as the local `YYYY-MM-DD` (`formatLaravelDate`) and is
 *   never converted to UTC.
 * - Showing dates to a person: every date a person reads goes through
 *   `formatDisplayDate` (or `formatDisplayDateRange`), in the device region's
 *   format with English words. `.format('…')` is for machine formats only
 *   (`YYYY-MM-DD` keys, request params); the `do-dates/no-display-format`
 *   rule enforces it. Also:
 *   - "N days ago" copy uses the `recent` style, not a hand-built count.
 *   - A validation message names a date with `dateNumeric`, the way the
 *     browser's date picker shows it (its value is always `yyyy-mm-dd`).
 *   - The API sends calendar days as `YYYY-MM-DD` under `_on` keys (`_at` is
 *     only ever an instant). A day that may still arrive as midnight UTC (a goal's
 *     `estimated_completion`) needs `{ calendarDate: true }`; a bare
 *     `YYYY-MM-DD` reads as a day by itself.
 *   - `setDisplayLocale` sets the region and clock: web passes
 *     `navigator.language`; mobile passes the phone's region and 24-Hour Time
 *     switch from expo-localization (`features/locale/displayLocale.ts`).
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
import dayOfYear from 'dayjs/plugin/dayOfYear.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';
import relativeTime from 'dayjs/plugin/relativeTime.js';
import updateLocale from 'dayjs/plugin/updateLocale.js';

import { DATE_CONSTANTS } from '../config/date-constants';

dayjs.extend(relativeTime);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(updateLocale);
dayjs.extend(dayOfYear);
dayjs.extend(customParseFormat);

// Configure Monday as the start of the week (to match Carbon in Laravel API)
dayjs.updateLocale('en', {
  weekStart: 1,
});

/**
 * Anything a date parameter accepts.
 *
 * Parameters take this; held and returned values are `dayjs.Dayjs`. Deliberately
 * narrower than `dayjs.ConfigType`, which also admits `null`, `undefined` and
 * numbers: `dayjs(undefined)` is now, so a forgotten argument silently becomes
 * today, and `dayjs(null)` is Invalid Date. Keeping them out lets the compiler
 * catch both. `Date` stays in so a value handed over by a library still fits.
 */
// eslint-disable-next-line do-dates/no-native-date -- the sanctioned entry point for a library's Date
export type DateInput = string | Date | dayjs.Dayjs;

/**
 * Format a date to the Laravel API format (e.g., "2024-01-15")
 */
export const formatLaravelDate = (date: DateInput): string => {
  return dayjs(date).format(DATE_CONSTANTS.DATE_FORMAT);
};

/**
 * Check if a date is today
 */
export const isToday = (date: string): boolean => {
  return dayjs(date).isSame(dayjs(), 'day');
};

/**
 * Check if a date is yesterday
 */
export const isYesterday = (date: string): boolean => {
  return dayjs(date).isSame(dayjs().subtract(1, 'day'), 'day');
};

/**
 * Get the start of the week for a given date (Monday)
 */
export const getStartOfWeek = (date: DateInput = dayjs()): dayjs.Dayjs => {
  return dayjs(date).startOf('week');
};

/**
 * Get the end of the week for a given date (Sunday)
 */
export const getEndOfWeek = (date: DateInput = dayjs()): dayjs.Dayjs => {
  return dayjs(date).endOf('week');
};

/**
 * Milliseconds from `now` to the next local midnight — how long a "today"
 * reading stays correct. The timer the platform `useToday` hooks arm.
 */
export const msUntilNextMidnight = (now: dayjs.Dayjs = dayjs()): number =>
  now.add(1, 'day').startOf('day').diff(now);

/**
 * Get greeting based on the current time
 */
export const getGreeting = (): string => {
  const hour = dayjs().hour();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
};

/**
 * Format a date to standard date format (YYYY-MM-DD)
 */
export const formatStandardDate = (date: DateInput): string => {
  return dayjs(date).format(DATE_CONSTANTS.DATE_FORMAT);
};

/**
 * Format a date to standard time format (HH:mm:ss)
 */
export const formatStandardTime = (date: DateInput): string => {
  return dayjs(date).format(DATE_CONSTANTS.TIME_FORMAT);
};

/**
 * Format a date to ISO datetime format
 */
export const formatISODateTime = (date: DateInput): string => {
  return dayjs(date).format(DATE_CONSTANTS.DATETIME_FORMAT);
};

/**
 * Parse a date using standard date format
 */
export const parseStandardDate = (dateString: string): dayjs.Dayjs => {
  return dayjs(dateString, DATE_CONSTANTS.DATE_FORMAT);
};

/**
 * Get current date in standard format
 */
export const getCurrentDate = (): string => {
  return dayjs().format(DATE_CONSTANTS.DATE_FORMAT);
};

/**
 * Get current time in standard format
 */
export const getCurrentTime = (): string => {
  return dayjs().format(DATE_CONSTANTS.TIME_FORMAT);
};

/**
 * Get current datetime in ISO format
 */
export const getCurrentDateTime = (): string => {
  return dayjs().format(DATE_CONSTANTS.DATETIME_FORMAT);
};

// =============================================================================
// DISPLAY DATES
// =============================================================================

/**
 * The closed list of display presets. A call site snaps to the nearest one, the
 * way a raw value snaps to the nearest design token; a new style needs a reason.
 * Examples (Thu 1 Oct 2026, 14:05) are en-GB / en-US; see
 * docs/prd/prd-display-dates.complete.md for the full table.
 */
export type DisplayDateStyle =
  | 'date' // 1 Oct 2026 / Oct 1, 2026
  | 'dateShort' // 1 Oct / Oct 1
  | 'dateLong' // 1 October 2026 / October 1, 2026
  | 'weekdayDate' // Thu 1 Oct / Thu, Oct 1
  | 'weekdayDateLong' // Thursday 1 October / Thursday, October 1
  | 'monthYear' // Oct 2026
  | 'monthYearLong' // October 2026
  | 'month' // Oct
  | 'day' // 1
  | 'weekday' // Thursday
  | 'weekdayShort' // Thu
  | 'weekdayNarrow' // T
  | 'time' // 14:05 / 2:05 PM
  | 'dateTime' // 1 Oct 2026, 14:05 / Oct 1, 2026, 2:05 PM
  | 'weekdayDateTime' // Thursday, 1 October 2026 at 14:05
  | 'dateNumeric' // 01/10/2026 / 10/01/2026, as the browser's date picker shows it
  | 'relative' // 3 hours ago
  | 'recent' // `relative` for up to 7 days, then `date`
  | 'relativeShort'; // just now · 5m ago · 2h ago · 3d ago, then `dateShort` / `date`

export interface DisplayDateOptions {
  /**
   * A calendar date the API may still send as midnight UTC (a goal's
   * `estimated_completion`). Read the day part and never convert it; a bare
   * `YYYY-MM-DD` (`target_date`, the `_on` days) reads as a
   * day with or without it.
   */
  calendarDate?: boolean;
}

type IntlStyle = Exclude<
  DisplayDateStyle,
  'relative' | 'recent' | 'relativeShort'
>;

// Component options only: `dateStyle`/`timeStyle` are not trusted on Hermes.
// `hour` is filled in per locale (see `hourOption`).
const INTL_STYLES: Record<IntlStyle, Intl.DateTimeFormatOptions> = {
  date: { day: 'numeric', month: 'short', year: 'numeric' },
  dateShort: { day: 'numeric', month: 'short' },
  dateLong: { day: 'numeric', month: 'long', year: 'numeric' },
  weekdayDate: { weekday: 'short', day: 'numeric', month: 'short' },
  weekdayDateLong: { weekday: 'long', day: 'numeric', month: 'long' },
  monthYear: { month: 'short', year: 'numeric' },
  monthYearLong: { month: 'long', year: 'numeric' },
  month: { month: 'short' },
  day: { day: 'numeric' },
  weekday: { weekday: 'long' },
  weekdayShort: { weekday: 'short' },
  weekdayNarrow: { weekday: 'narrow' },
  time: { minute: '2-digit' },
  dateTime: {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    minute: '2-digit',
  },
  weekdayDateTime: {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    minute: '2-digit',
  },
  dateNumeric: { day: '2-digit', month: '2-digit', year: 'numeric' },
};

const TIMED_STYLES: ReadonlySet<IntlStyle> = new Set([
  'time',
  'dateTime',
  'weekdayDateTime',
]);

// A calendar date has no time: a timed style falls back to its date part.
const CALENDAR_STYLE: Partial<Record<IntlStyle, IntlStyle | null>> = {
  time: null,
  dateTime: 'date',
  weekdayDateTime: 'weekdayDateLong',
};

// Shapes are checked before parsing: dayjs `isValid()` accepts 'Week 1'.
const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const INSTANT_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;
const NAIVE_RE = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
const REGION_RE = /^([A-Za-z]{2}|\d{3})$/;

let displayLocale: string | null = null;
// The device's own 12/24-hour choice, or null for the region's usual clock.
let hour12Choice: boolean | null = null;
let hour24: boolean | null = null;
const formatters = new Map<string, Intl.DateTimeFormat>();

/**
 * `en-<region>` from a device tag (`fr-FR` → `en-FR`, `zh-Hant-TW` → `en-TW`),
 * or `en` without one. Parsed by hand: Hermes has no `Intl.Locale`.
 */
const toDisplayLocale = (tag: string | undefined): string => {
  for (const part of (tag ?? '').split(/[-_]/).slice(1)) {
    if (part.length === 1) break; // an extension (-u-, -x-) ends the region search
    if (REGION_RE.test(part)) return `en-${part.toUpperCase()}`;
  }
  return 'en';
};

const getDisplayLocale = (): string => {
  displayLocale ??= toDisplayLocale(
    new Intl.DateTimeFormat().resolvedOptions().locale
  );
  return displayLocale;
};

export interface DisplayLocaleOptions {
  /**
   * The device's own 12/24-hour choice, when the platform can read it. Hermes'
   * Intl ignores the iOS 24-Hour Time switch, so mobile reads it with
   * expo-localization and passes it here. Omitted, times use the region's
   * usual clock.
   */
  hour12?: boolean;
}

/**
 * Set the display locale from the device's locale tag, at startup and whenever
 * the device's settings may have changed. The region picks the format; the
 * words stay English. Clears the formatter cache.
 */
export const setDisplayLocale = (
  deviceLocale: string | undefined,
  options: DisplayLocaleOptions = {}
): void => {
  displayLocale = toDisplayLocale(deviceLocale);
  hour12Choice = options.hour12 ?? null;
  hour24 = null;
  formatters.clear();
};

// '09:12' on a 24-hour clock, '9:12 AM' on a 12-hour one: the device's own
// choice when it was given, otherwise the region's usual clock.
const hourOption = (locale: string): Intl.DateTimeFormatOptions['hour'] => {
  if (hour24 === null) {
    if (hour12Choice === null) {
      const { hourCycle } = new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
      }).resolvedOptions();
      hour24 = hourCycle === 'h23' || hourCycle === 'h24';
    } else {
      hour24 = !hour12Choice;
    }
  }
  return hour24 ? '2-digit' : 'numeric';
};

const getFormatter = (style: IntlStyle): Intl.DateTimeFormat => {
  const locale = getDisplayLocale();
  const key = `${locale}|${style}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    const options = TIMED_STYLES.has(style)
      ? {
          ...INTL_STYLES[style],
          hour: hourOption(locale),
          ...(hour12Choice === null ? {} : { hour12: hour12Choice }),
        }
      : INTL_STYLES[style];
    formatter = new Intl.DateTimeFormat(locale, options);
    formatters.set(key, formatter);
  }
  return formatter;
};

const formatIntl = (value: dayjs.Dayjs, style: IntlStyle): string =>
  getFormatter(style).format(value.valueOf());

// A calendar day, fixed at local noon so a DST change at midnight can't move it.
const parseCalendarDate = (day: string): dayjs.Dayjs | null => {
  const parsed = dayjs(day, DATE_CONSTANTS.DATE_FORMAT, true);
  return parsed.isValid() ? parsed.hour(12) : null;
};

const parseDisplayValue = (
  value: DateInput,
  calendarDate: boolean
): { date: dayjs.Dayjs; isCalendar: boolean } | null => {
  if (typeof value !== 'string') {
    const date = dayjs(value);
    if (!date.isValid()) return null;
    return calendarDate
      ? { date: date.startOf('day').hour(12), isCalendar: true }
      : { date, isCalendar: false };
  }

  const isInstant = INSTANT_RE.test(value);
  const isNaive = NAIVE_RE.test(value);

  if (
    CALENDAR_DATE_RE.test(value) ||
    (calendarDate && (isInstant || isNaive))
  ) {
    const date = parseCalendarDate(value.slice(0, 10));
    return date ? { date, isCalendar: true } : null;
  }

  // The API's timezone is UTC, so a naive date-time is read as UTC.
  const date = isInstant
    ? dayjs(value)
    : isNaive
      ? dayjs(`${value.replace(' ', 'T')}Z`)
      : null;
  return date?.isValid() ? { date, isCalendar: false } : null;
};

// Whole days from today to a calendar date (both at local noon).
const daysSince = (date: dayjs.Dayjs, now: dayjs.Dayjs): number =>
  now.startOf('day').hour(12).diff(date, 'day');

// 'today', 'yesterday', '3 days ago': whole days, never hours.
const relativeDays = (date: dayjs.Dayjs, now: dayjs.Dayjs): string => {
  const days = daysSince(date, now);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days === -1) return 'tomorrow';
  return date.from(now.startOf('day').hour(12));
};

const formatRelativeShort = (
  date: dayjs.Dayjs,
  isCalendar: boolean,
  now: dayjs.Dayjs
): string => {
  const fallback = (): string =>
    formatIntl(date, date.isSame(now, 'year') ? 'dateShort' : 'date');

  if (isCalendar) {
    const days = daysSince(date, now);
    if (days === 0) return 'today';
    return days > 0 && days < 7 ? `${days}d ago` : fallback();
  }

  const minutes = now.diff(date, 'minute');
  if (minutes < -1) return fallback(); // in the future, beyond clock skew
  // Lower case like '5m ago', so it reads right mid-sentence ('Asked just now').
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = now.diff(date, 'hour');
  if (hours < 24) return `${hours}h ago`;
  const days = now.diff(date, 'day');
  return days < 7 ? `${days}d ago` : fallback();
};

/**
 * Format a date for a person to read, in the device region's format with
 * English words. Every date a person reads goes through this; machine formats
 * (`YYYY-MM-DD` keys, request params, `toISOString()`) do not.
 *
 * Accepts an API instant (`…000000Z` or `±HH:MM`, shown in the device's
 * timezone), a `YYYY-MM-DD` calendar date (never converted; timed styles drop
 * the time and `time` returns null), a naive `YYYY-MM-DD HH:mm:ss` (read as
 * UTC), or a `Dayjs`/`Date` as it is. Anything else returns null, and the
 * caller picks the placeholder.
 *
 * @example formatDisplayDate(note.date, 'relative') // '3 hours ago'
 * @example formatDisplayDate(goal.target_date, 'date', { calendarDate: true }) // '1 Oct 2026'
 */
export const formatDisplayDate = (
  value: DateInput | null | undefined,
  style: DisplayDateStyle = 'date',
  options?: DisplayDateOptions
): string | null => {
  if (value === null || value === undefined) return null;
  const parsed = parseDisplayValue(value, options?.calendarDate ?? false);
  if (!parsed) return null;
  const { date, isCalendar } = parsed;

  if (style === 'relative' || style === 'recent') {
    const now = dayjs();
    const days = isCalendar ? daysSince(date, now) : now.diff(date, 'day');
    if (style === 'recent' && days > 7) return formatIntl(date, 'date');
    return isCalendar ? relativeDays(date, now) : date.fromNow();
  }
  if (style === 'relativeShort')
    return formatRelativeShort(date, isCalendar, dayjs());

  if (isCalendar && style in CALENDAR_STYLE) {
    const calendarStyle = CALENDAR_STYLE[style];
    return calendarStyle ? formatIntl(date, calendarStyle) : null;
  }
  return formatIntl(date, style);
};

/**
 * Format a date range in the region's order, shortening what the two ends
 * share ('5 – 11 Oct' in en-GB, 'Oct 5 – 11' in en-US). Both ends are read the
 * way `formatDisplayDate` reads them; if either can't be read, it returns null.
 * A runtime without `Intl.DateTimeFormat#formatRange` gets two full ends.
 *
 * @example formatDisplayDateRange(weekStart, weekEnd) // '28 Sept – 4 Oct'
 */
export const formatDisplayDateRange = (
  start: DateInput | null | undefined,
  end: DateInput | null | undefined,
  style: 'date' | 'dateShort' = 'dateShort',
  options?: DisplayDateOptions
): string | null => {
  if (start === null || start === undefined) return null;
  if (end === null || end === undefined) return null;
  const calendarDate = options?.calendarDate ?? false;
  const from = parseDisplayValue(start, calendarDate);
  const to = parseDisplayValue(end, calendarDate);
  if (!from || !to) return null;

  const formatter = getFormatter(style);
  if (typeof formatter.formatRange !== 'function') {
    return `${formatter.format(from.date.valueOf())} – ${formatter.format(to.date.valueOf())}`;
  }
  return formatter.formatRange(from.date.valueOf(), to.date.valueOf());
};

/**
 * Re-export dayjs
 *
 * Use this dayjs instance for all date operations,
 * it includes app specific configuration and plugins.
 */
export { dayjs };
